import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios, { isCancel } from '../utils/axios';
import {
  DEFAULT_LIST_PAGE,
  DEFAULT_LIST_PAGE_SIZE,
  DEFAULT_LOOKUP_PAGE_SIZE,
} from '../constants';
import { createRequestError, isSsoReauthRequired, isSsoTemporarilyUnavailable } from '../utils/requestErrors';
import { resolveProductFields } from '../utils/resolveProductFields';
import type { PackageCoaOption, Product } from '../wysiwyg/packageUtils';
import { normalizeCoaKeys } from '../wysiwyg/coaUtils';
import type {
  AvailableTagRecord,
  PackageRecord,
  PackageTotalsParams,
  PagedResult,
  PrintRunRecord,
  StagedPackageRecord,
  StagedPackageTotalsParams,
} from '../types/index';

function normalizePackageList(packages: PackageRecord[] = []): PackageRecord[] {
  return (packages || []).map((item) => ({
    ...item,
    id: item?.id ?? item?.packageRowId,
    label: item?.label ?? item?.packageLabel ?? '',
  }));
}

function parsePositiveIntegerId(value: number | string, fieldName: string): number {
  const trimmedValue = typeof value === 'string' ? value.trim() : value;
  const parsedValue = typeof trimmedValue === 'string' && /^\d+$/.test(trimmedValue)
    ? Number(trimmedValue)
    : trimmedValue;

  if (typeof parsedValue !== 'number' || !Number.isSafeInteger(parsedValue) || parsedValue <= 0) {
    throw new TypeError(`${fieldName} must be a positive integer.`);
  }

  return parsedValue;
}

function normalizePagedPackages(
  responseData: any,
  page: number,
  pageSize: number
): PagedResult<PackageRecord> {
  return {
    page: Number(responseData?.page ?? page),
    pageSize: Number(responseData?.pageSize ?? pageSize),
    total: Number(responseData?.total ?? 0),
    data: normalizePackageList(Array.isArray(responseData?.data) ? responseData.data : []),
  };
}

function normalizePagedStagedPackages(
  responseData: any,
  page: number,
  pageSize: number
): PagedResult<StagedPackageRecord> {
  return {
    page: Number(responseData?.page ?? page),
    pageSize: Number(responseData?.pageSize ?? pageSize),
    total: Number(responseData?.total ?? 0),
    data: Array.isArray(responseData?.data) ? responseData.data : [],
  };
}

export async function getPackagesTotals({
  organizationId,
  label = null,
  searchTerm = null,
  facilityId = null,
  marketId = null,
  importedAtSortAscending = false,
  sortField = null,
  sortAscending = null,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LIST_PAGE_SIZE,
}: PackageTotalsParams = {}, signal?: AbortSignal): Promise<PagedResult<PackageRecord>> {
  const response = await axios.get('/package/totals', {
    params: {
      organizationId,
      label,
      searchTerm,
      facilityId,
      marketId,
      importedAtSortAscending,
      sortField,
      sortAscending,
      page,
      pageSize,
    },
    signal,
  });

  return normalizePagedPackages(response.data, page, pageSize);
}

export function getPackageTotalsAbortable({
  organizationId,
  label = null,
  facilityId = null,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LIST_PAGE_SIZE,
}: Pick<PackageTotalsParams, 'organizationId' | 'label' | 'facilityId' | 'page' | 'pageSize'>) {
  const controller = new AbortController();
  const promise = axios.get('/package/totals', {
    params: { organizationId, label, facilityId, page, pageSize },
    signal: controller.signal,
  }).then((response) => normalizePagedPackages(response.data, page, pageSize));

  return {
    promise,
    cancel: () => controller.abort(),
    signal: controller.signal,
  };
}

type UsePrintPackageSearchOptions = {
  organizationId?: number | string | null;
  search: string;
  enabled: boolean;
  initialPackage?: PackageRecord | null;
  initialPackageLabel?: string;
  products?: ReadonlyArray<Partial<Product>> | null;
};

type UsePrintPackageSearchResult = {
  packages: PackageCoaOption[];
  isLoading: boolean;
  error: unknown;
  retry: () => void;
};

function normalizePrintPackageOption(
  packageRecord: PackageRecord,
  labelOverride?: string,
): PackageCoaOption | null {
  const rawPackage = packageRecord as PackageRecord & Record<string, any>;
  const id = String(rawPackage.id ?? rawPackage.packageId ?? rawPackage.packageRowId ?? rawPackage.PackageId ?? '');
  if (!id || id === 'undefined') return null;

  const label = labelOverride || rawPackage.label || rawPackage.packageLabel || rawPackage.PackageLabel || `Package ${id}`;
  const facility = rawPackage.facility || rawPackage.Facility;
  const facilityName = rawPackage.facilityName || rawPackage.FacilityName || facility?.name || facility?.Name || '';
  const facilityLicense = rawPackage.facilityLicense || rawPackage.FacilityLicense || rawPackage.license || rawPackage.License || facility?.license || facility?.License || '';
  const facilityId = rawPackage.facilityId ?? rawPackage.FacilityId ?? facility?.id ?? facility?.Id ?? null;
  const productId = Number(rawPackage.productId ?? rawPackage.ProductId);

  return {
    id,
    label,
    facilityName,
    facilityLicense,
    facilityId,
    productId: Number.isFinite(productId) && productId > 0 ? productId : undefined,
    metrcPackageId: rawPackage.metrcPackageId == null && rawPackage.MetrcPackageId == null
      ? null
      : Number(rawPackage.metrcPackageId ?? rawPackage.MetrcPackageId),
    labTestingStateName: rawPackage.labTestingStateName || rawPackage.LabTestingStateName || '',
    coaData: {
      ...normalizeCoaKeys(packageRecord),
      packageTag: label,
      productName: rawPackage.productName || rawPackage.ProductName || '',
      facilityName,
      facilityLicense,
      facilityId,
      strain: rawPackage.strain || '',
      productCategoryName: rawPackage.productCategoryName || '',
      quantity: rawPackage.quantity || 0,
      packagedDate: rawPackage.packagedDate || rawPackage.rawPackageDates?.packagedDate,
      labTestingStateName: rawPackage.labTestingStateName || rawPackage.LabTestingStateName || '',
    },
    marketCode: rawPackage.marketCode || rawPackage.MarketCode || '',
  };
}

/** Owns package search request state so callers only render results and select a package. */
export function usePrintPackageSearch({
  organizationId,
  search,
  enabled,
  initialPackage = null,
  initialPackageLabel = '',
  products,
}: UsePrintPackageSearchOptions): UsePrintPackageSearchResult {
  const initialOption = useMemo(
    () => initialPackage
      ? normalizePrintPackageOption(initialPackage, initialPackageLabel)
      : null,
    [initialPackage, initialPackageLabel],
  );
  const requestKey = JSON.stringify([String(organizationId ?? ''), search, enabled]);
  const [state, setState] = useState<{
    key: string;
    packages: PackageCoaOption[];
    error: unknown;
    isLoading: boolean;
  }>({ key: requestKey, packages: initialOption ? [initialOption] : [], error: null, isLoading: false });
  const [reloadToken, setReloadToken] = useState(0);
  const retry = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    if (!enabled || !organizationId) {
      setState({
        key: requestKey,
        packages: initialOption ? [initialOption] : [],
        error: null,
        isLoading: false,
      });
      return;
    }

    const controller = new AbortController();
    const request = getPackagesTotals({
      organizationId,
      label: search,
      page: 0,
      pageSize: 10,
    }, controller.signal);
    let settled = false;
    setState({ key: requestKey, packages: [], error: null, isLoading: true });

    request
      .then((response) => {
        if (settled) return;
        const packages = response.data
          .map((packageRecord) => normalizePrintPackageOption(packageRecord))
          .filter((packageRecord): packageRecord is PackageCoaOption => packageRecord !== null);
        setState({ key: requestKey, packages, error: null, isLoading: false });
      })
      .catch((error) => {
        if (settled || isCancel(error)) return;
        setState({ key: requestKey, packages: [], error, isLoading: false });
      });

    return () => {
      settled = true;
      controller.abort();
    };
  }, [enabled, initialOption, organizationId, requestKey, reloadToken, search]);

  // Resolve against the current organization's product list at render time, so
  // late product hydration cannot leave stale names or repeat package requests.
  const packages = useMemo(() => {
    if (state.key !== requestKey) return [];
    return state.packages.map((option) => {
      const linkedProduct = option.productId && Array.isArray(products)
        ? products.find((product) => Number(product?.id) === option.productId)
        : null;
      const resolved = resolveProductFields(null, linkedProduct, option.coaData);
      return {
        ...option,
        coaData: { ...option.coaData, ...resolved },
      };
    });
  }, [state.key, state.packages, requestKey, products]);

  return {
    packages,
    isLoading: state.key === requestKey && state.isLoading,
    error: state.key === requestKey ? state.error : null,
    retry,
  };
}

export async function getAvailablePackages({
  organizationId,
  marketId,
  facilityId,
  label = null,
  productName = null,
  batchNumber = null,
  category = null,
  sourceNumber = null,
  testedDate = null,
  includeImported = true,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LIST_PAGE_SIZE,
}: {
  organizationId?: number | string | null;
  marketId?: number | string | null;
  facilityId?: number | string | null;
  label?: string | null;
  productName?: string | null;
  batchNumber?: string | null;
  category?: string | null;
  sourceNumber?: string | null;
  testedDate?: string | null;
  includeImported?: boolean;
  page?: number;
  pageSize?: number;
}): Promise<{ documents: PackageRecord[]; total?: number | string | null }> {
  const response = await axios.get('/package/available', {
    params: {
      organizationId,
      marketId,
      facilityId,
      label,
      productName,
      batchNumber,
      category,
      sourceNumber,
      testedDate,
      includeImported,
      page,
      pageSize,
    },
  });

  if (response.data?.error) {
    throw Object.assign(new Error('Could not retrieve available packages'), response.data);
  }

  const documents = Array.isArray(response.data?.documents)
    ? response.data.documents
    : (Array.isArray(response.data) ? response.data : []);

  return {
    documents: normalizePackageList(documents),
    total: response.data?.total,
  };
}

export async function importPackage(payload: {
  organizationId?: number | string | null;
  facilityId?: number | string | null;
  productId?: number | string | null;
  metrcPackageId?: number | string | null;
  useArchivedProduct?: boolean;
}): Promise<unknown> {
  const response = await axios.post('/package/import', payload);
  return response.data;
}

export async function importPackageCheckArchivedProducts(payload: {
  organizationId?: number | string | null;
  facilityId?: number | string | null;
  packages: Array<{
    metrcPackageId?: number | string | null;
    productId?: number | string | null;
  }>;
}): Promise<any> {
  try {
    const response = await axios.post('/package/import/check-archived-products', payload);
    return response.data;
  } catch (error) {
    throw createRequestError(error);
  }
}

export async function addPackage(payload: unknown): Promise<unknown> {
  const response = await axios.post('/package/add', payload);
  return response.data;
}

export async function editPackage(payload: unknown): Promise<unknown> {
  const response = await axios.post('/package/edit', payload);
  return response.data;
}

export async function addPackageStaged(payload: unknown): Promise<unknown> {
  const response = await axios.post('/package/staged/add', payload);
  return response.data;
}

export type StageTagsOutcome = 'staged' | 'partial' | 'failed';
export type StageTagsResult = {
  outcome: StageTagsOutcome;
  stagedTagIds: Array<number | string>;
  failedTagIds: Array<number | string>;
  // Set when a tag failed because no Metrc sign-in works (SSO organizations only), so the caller can prompt once.
  ssoError?: unknown;
};
export type StageableTagRecord = AvailableTagRecord & { id: number | string };
export const isStageableTag = (tag: AvailableTagRecord): tag is StageableTagRecord =>
  tag.id !== undefined && tag.id !== null && tag.id !== '';
export type UseStageTagsResult = {
  submit: (tags: StageableTagRecord[]) => Promise<StageTagsResult | null>;
  isPending: boolean;
};

// The staging endpoint confirms creation with its Int64 record ID.
const isStagedPackageId = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0;

/** Owns the staging request so the dialog stays presentation-only. */
export function useStageTags({
  organizationId,
  facilityId,
  metrcFacilityId,
}: {
  organizationId?: number | string | null;
  facilityId?: number | string | null;
  metrcFacilityId?: number | string | null;
}): UseStageTagsResult {
  const inFlightRef = useRef(false);
  const [isPending, setIsPending] = useState(false);
  const submit = useCallback(async (tags: StageableTagRecord[]): Promise<StageTagsResult | null> => {
    if (!tags.length || inFlightRef.current) return null;
    inFlightRef.current = true;
    setIsPending(true);
    try {
      const results = await Promise.allSettled(tags.map((tag) => addPackageStaged({
        organizationId,
        packageLabel: tag.label,
        packageId: tag.packageId,
        productId: tag.productId,
        organizationFacilityId: facilityId,
        facilityId: metrcFacilityId ?? tag.facilityId ?? facilityId,
      })));
      const stagedTagIds: Array<number | string> = [];
      const failedTagIds: Array<number | string> = [];
      let ssoError: unknown;
      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && isStagedPackageId(result.value)) {
          stagedTagIds.push(tags[index].id);
        } else {
          failedTagIds.push(tags[index].id);
          if (result.status === 'rejected' && ssoError === undefined
            && (isSsoReauthRequired(result.reason) || isSsoTemporarilyUnavailable(result.reason))) {
            ssoError = result.reason;
          }
        }
      });
      return {
        outcome: failedTagIds.length === 0 ? 'staged' : stagedTagIds.length === 0 ? 'failed' : 'partial',
        stagedTagIds,
        failedTagIds,
        ...(ssoError === undefined ? {} : { ssoError }),
      };
    } finally {
      inFlightRef.current = false;
      setIsPending(false);
    }
  }, [organizationId, facilityId, metrcFacilityId]);

  return { submit, isPending };
}

export async function editPackageStaged(payload: unknown): Promise<unknown> {
  const response = await axios.post('/package/staged/edit', payload);
  return response.data;
}

export async function getPackagesStaged(payload: StagedPackageTotalsParams = {}): Promise<StagedPackageRecord[]> {
  const response = await axios.get('/package/staged', {
    params: payload,
  });

  if (Array.isArray(response.data)) {
    return response.data;
  }

  if (Array.isArray(response.data?.data)) {
    return response.data.data;
  }

  return [];
}

export async function getPackagesStagedTotals({
  organizationId,
  label = null,
  searchTerm = null,
  facilityId = null,
  marketId = null,
  importedAtSortAscending = false,
  sortField = null,
  sortAscending = null,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LIST_PAGE_SIZE,
}: StagedPackageTotalsParams = {}): Promise<PagedResult<StagedPackageRecord>> {
  const response = await axios.get('/package/staged/totals', {
    params: {
      organizationId,
      label,
      searchTerm,
      facilityId,
      marketId,
      importedAtSortAscending,
      sortField,
      sortAscending,
      page,
      pageSize,
    },
  });

  return normalizePagedStagedPackages(response.data, page, pageSize);
}

export async function getPackageStagedById(
  organizationId: number | string,
  packageLabel: string,
  facilityId?: number | string | null,
): Promise<StagedPackageRecord | null> {
  const response = await axios.get('/package/staged/byid', {
    params: {
      organizationId,
      packageLabel,
      ...(facilityId != null ? { facilityId } : {}),
    },
  });
  return response.data || null;
}

export async function getPackageById(
  organizationId: number | string,
  packageId: number | string
): Promise<PackageRecord | null> {
  const response = await axios.get('/package/byid', {
    params: { organizationId, packageId },
  });
  return response.data || null;
}

export type RestoredPackageParams = {
  /** Legacy inline display data; server hydration remains authoritative. */
  stagedPackage?: { label?: string; productName?: string } | null;
  printRun?: PrintRunRecord | null;
  packageId?: number | string | null;
  packageLabel?: string;
  facilityId?: number | string | null;
  isStaged?: boolean;
};

export type RestoredPackageLookup = {
  isStaged: boolean;
  packageId: number | string | null;
  packageLabel: string;
  hasPackageId: boolean;
};

/** Shared so the container's request and the dialog's presentation mapping cannot diverge. */
export function resolveRestoredPackageLookup({
  printRun = null,
  packageId = null,
  packageLabel = '',
  isStaged,
}: RestoredPackageParams = {}): RestoredPackageLookup {
  const resolvedPackageLabel =
    printRun?.packageLabel || printRun?.packageTag || packageLabel || '';
  const packageIdForLookup =
    printRun?.packageId != null && String(printRun.packageId) !== ''
      ? printRun.packageId
      : packageId;
  const hasPackageId = packageIdForLookup != null && String(packageIdForLookup) !== '';
  const resolvedIsStaged = isStaged ?? (!hasPackageId && resolvedPackageLabel.length > 0);

  return {
    isStaged: resolvedIsStaged,
    packageId: hasPackageId ? packageIdForLookup! : null,
    packageLabel: resolvedPackageLabel,
    hasPackageId,
  };
}

export type UseRestoredPrintPackageResult = {
  data: unknown | null;
  isLoading: boolean;
  error: unknown;
  /** Reruns the request for the current key; no-op when nothing is being loaded. */
  retry: () => void;
};

export function restoreStagedDraftPackage(printRun: PrintRunRecord): StagedPackageRecord {
  const context = printRun?.stagedPrintContext;
  const savedId = Number(printRun?.stagePackageCoaId);
  const contextId = Number(context?.stagedCoaId);
  const facilityId = Number(context?.facilityId);
  const label = String(printRun?.packageLabel || printRun?.packageTag || '');
  if (!context || !Number.isSafeInteger(savedId) || savedId <= 0 || contextId !== savedId
    || !Number.isSafeInteger(facilityId) || facilityId <= 0
    || !label || String(context.packageLabel || '').toLowerCase() !== label.toLowerCase()
    || !Object.prototype.hasOwnProperty.call(context, 'stagedCoaData')
    || (context.stagedCoaData !== null && typeof context.stagedCoaData !== 'string')) {
    throw new Error('The saved staged COA could not be loaded.');
  }
  return {
    id: savedId,
    sourcePackageId: context.sourcePackageId,
    stagedCoaId: savedId,
    stagedCoaData: context.stagedCoaData,
    label: context.packageLabel,
    facilityId,
    productId: context.productId ?? undefined,
    productName: context.productName || printRun.productName || '',
  };
}

/** Owns the package request for a restored print run so the dialog stays presentation-only. */
export function useRestoredPrintPackage(
  organizationId?: number | string | null,
  params: RestoredPackageParams = {},
): UseRestoredPrintPackageResult {
  const { isStaged, packageId, packageLabel, hasPackageId } =
    resolveRestoredPackageLookup(params);
  const facilityId = params.facilityId;
  const printRun = params.printRun;
  const printRunRef = useRef(printRun);
  printRunRef.current = printRun;
  const restoreSavedDraft = isStaged && (printRun?.status === 4 || printRun?.status === 'Draft');
  const savedContextKey = restoreSavedDraft
    ? JSON.stringify([printRun?.id, printRun?.stagePackageCoaId, printRun?.productName, printRun?.stagedPrintContext ?? null])
    : '';

  const hasOrganization =
    organizationId !== null && organizationId !== undefined && organizationId !== '';
  const loadStaged = isStaged && packageLabel !== '';
  const loadActive = !isStaged && hasPackageId;
  const enabled = hasOrganization && (loadStaged || loadActive);

  // Identity of the record being resolved; a result is only ever exposed for the current key.
  const requestKey = JSON.stringify([
    String(organizationId ?? ''),
    isStaged,
    isStaged ? packageLabel : String(packageId ?? ''),
    isStaged ? String(facilityId ?? '') : '',
    savedContextKey,
  ]);

  const [state, setState] = useState<{
    key: string | null;
    data: unknown | null;
    error: unknown;
    isLoading: boolean;
  }>({ key: null, data: null, error: null, isLoading: false });

  const [reloadToken, setReloadToken] = useState(0);
  const retry = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    if (!enabled) {
      setState({ key: requestKey, data: null, error: null, isLoading: false });
      return;
    }

    let cancelled = false;
    setState({ key: requestKey, data: null, error: null, isLoading: true });

    const savedRun = printRunRef.current;
    const request = restoreSavedDraft && savedRun
      ? Promise.resolve().then(() => restoreStagedDraftPackage(savedRun))
      : loadStaged
        ? getPackageStagedById(organizationId as number | string, packageLabel, facilityId)
        : getPackageById(organizationId as number | string, packageId as number | string);

    request
      .then((result) => {
        if (cancelled) return;
        if (result == null || result === '') throw new Error('Package could not be loaded.');
        setState({ key: requestKey, data: result, error: null, isLoading: false });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ key: requestKey, data: null, error: err, isLoading: false });
      });

    return () => {
      cancelled = true;
    };
  }, [requestKey, enabled, loadStaged, organizationId, packageId, packageLabel, facilityId, reloadToken, restoreSavedDraft]);

  const matchesCurrentKey = state.key === requestKey;

  return {
    data: matchesCurrentKey ? state.data : null,
    error: matchesCurrentKey ? state.error : null,
    isLoading: enabled && (!matchesCurrentKey || state.isLoading),
    retry,
  };
}

export async function getAvailableTags({
  organizationId,
  facilityId,
  tagLabel = null,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LOOKUP_PAGE_SIZE,
}: {
  organizationId?: number | string | null;
  facilityId?: number | string | null;
  tagLabel?: string | null;
  page?: number;
  pageSize?: number;
}): Promise<{ documents: AvailableTagRecord[]; total: number }> {
  const response = await axios.get('/package/tags/available', {
    params: { organizationId, facilityId, tagLabel, page, pageSize },
  });

  if (Array.isArray(response.data)) {
    return {
      documents: response.data,
      total: response.data.length,
    };
  }

  const documents = Array.isArray(response.data?.documents) ? response.data.documents : [];
  const parsedTotal = Number(response.data?.total);
  return {
    documents,
    total: Number.isFinite(parsedTotal) ? parsedTotal : documents.length,
  };
}

export async function getCOAByPackageId(
  organizationId: number | string,
  packageId: number | string
): Promise<any> {
  const response = await axios.get('/package/coa/byid', {
    params: { organizationId, packageId },
  });
  return response.data;
}

export async function getCoaFilesByLabel(label: string): Promise<any> {
  const response = await axios.get('/package/coa-files/by-label', {
    params: { label, includePrintRuns: true },
  });
  return response.data;
}

export async function getPackageSummary(label: string): Promise<unknown> {
  const response = await axios.get('/package/summary', { params: { label } });
  return response.data;
}

export async function getStagedPackageSummary(label: string): Promise<unknown> {
  const response = await axios.get('/package/staged/summary', { params: { label } });
  return response.data;
}

export async function promoteStagedPackage(payload: {
  organizationId: number;
  stagedLabel: string;
}): Promise<unknown> {
  const response = await axios.post('/package/promote/staged', {
    OrganizationId: payload.organizationId,
    StagedLabel: payload.stagedLabel,
  });
  return response.data;
}

export async function setCoaFileVisibility(payload: unknown): Promise<any> {
  const response = await axios.post('/package/coa-files/set-visibility', payload);
  return response.data;
}

export async function setCoaFileDisplayOrder(payload: unknown): Promise<any> {
  const response = await axios.post('/package/coa-files/display-order', payload);
  return response.data;
}

export async function addCoaFile(payload: unknown): Promise<any> {
  const response = await axios.post('/package/coa-files/add', payload);
  return response.data;
}

export async function deleteCoaFile(payload: unknown): Promise<any> {
  const response = await axios.post('/package/coa-files/delete', payload);
  return response.data;
}

export async function syncPackageCoa(
  organizationId: number | string,
  payload: unknown
): Promise<any> {
  const finalPayload =
    payload && typeof payload === 'object'
      ? payload
      : {
          OrganizationId: /^\d+$/.test(String(organizationId)) ? Number(String(organizationId)) : organizationId,
          PackageId: /^\d+$/.test(String(payload)) ? Number(String(payload)) : payload,
        };

  const response = await axios.post('/package/coa/sync', finalPayload);
  return response.data;
}

export async function syncPackageCoaMultiple(
  organizationId: number | string,
  packageIds: Array<number | string>,
): Promise<any> {
  const parsedOrganizationId = parsePositiveIntegerId(organizationId, 'OrganizationId');
  const parsedPackageIds = packageIds.map((packageId) =>
    parsePositiveIntegerId(packageId, 'PackageId'));
  const response = await axios.post('/package/coa/sync/multiple', {
    OrganizationId: parsedOrganizationId,
    PackageIds: parsedPackageIds,
  });
  return response.data;
}

export async function importProductV2(payload: {
  organizationId?: number | string | null;
  metrcProductId?: number | string | null;
  facilityId?: number | string | null;
  metrcPackageId?: number | string | null;
}): Promise<any> {
  const response = await axios.post('/product/import', payload);
  return response.data;
}

export { isCancel };
